// The Story room's reads and writes over the live project (Apps/Shared/Rooms/StoryRoom.swift's `StoryMemo`,
// LibraryModel+Story, +CharacterStudy, +Glossary, +Names, +Shape): who is in which scene (the index's presence rows,
// worked out here per fragment and kept while the fragment is unchanged), the people most present first, the scenes in
// reading order with their story times, continuity findings, story-time suggestions, the Shape, and the writes — each
// one `live.edit` through the core's `StoryEditor` (commit messages as the Mac's), plus the name generator's choices
// and the view choices the room's header controls share with its pages (per browser, localStorage).
import { useSyncExternalStore } from 'react';
import {
  DocumentEditor, STUDY_KINDS, utf16OffsetOfScalar, type OpenedProject, type Project, type ProjectEditor, type ArrangementInfo, type StudyKind,
} from '@writing-suite/core';
import {
  BUILT_IN_LISTS, calendarLanes, checkContinuity, chronology, CoPresence, decodeNameOptions, defaultNameOptions, explicitPresence,
  manuscriptCoPresence, parseNames, presence, readBeatSheet, readTravel, reference, sceneFacts, sceneShapeOf, shapeCSV, storyAppearances, storyPeople,
  storyScenes, storyTimeScenes, suggestStoryTimes, StoryEditor, type ChronologyGrouping, type ContinuityFinding, type NameList,
  type NameListKind, type NameOptions, type Presence, type PresenceRow, type SceneShape, type StoryScene, type StoryTimeProposal,
  type TravelSettings,
} from '@writing-suite/core/story';
import { ReviseEditor } from '@writing-suite/core/revise';
import type { LiveProject } from './project.ts';
import { saveFile } from '../host/host.ts';

export const opened = (p: Project) => p as OpenedProject;

// MARK: Writing

/** `StoryEditor` plus the one write the core hasn't: `setCharacterStudiesEnabled` (CharacterStudy.swift). */
export class WebStoryEditor extends StoryEditor {
  /** Study pages on or off ("Turn on/off character study pages"); turning them on makes the missing ones, as the
   *  Mac's reload does. */
  setCharacterStudies(on: boolean): string[] {
    return this.setStudyPages('character', on);
  }

  /** `setStudyPagesEnabled`: a kind's study pages on or off; turning them on makes the missing ones, as the Mac's
   *  reload does. */
  setStudyPages(kind: StudyKind, on: boolean): string[] {
    const changed = this.change('project', on ? `Turn on ${kind} study pages` : `Turn off ${kind} study pages`, this.now(), (w) => {
      w.ensure(['settings']);
      w.putIfChanged(['settings'], STUDY_KINDS[kind].settingsKey, { t: 'boolean', v: on });
    });
    if (on) this.ensureStudyPagesOf(kind);
    return changed ? ['project'] : [];
  }
}

/** A Story write: the core's `StoryEditor` over the documents `live.edit` hands its editor (one outbox batch per
 *  document it changes). */
export function storyEdit<T>(live: LiveProject, fn: (editor: WebStoryEditor) => T): T {
  return live.edit((e: ProjectEditor) => {
    const d = e as DocumentEditor;
    return fn(new WebStoryEditor(d.docs, d.context));
  });
}

/** Whether a kind's study pages are kept (`characterStudies`/`placeStudies`, default on except for journals). */
export function studyPagesOn(project: OpenedProject, kind: StudyKind = 'character'): boolean {
  const v = project.record.settings[STUDY_KINDS[kind].settingsKey];
  return typeof v === 'boolean' ? v : project.record.projectType !== 'journal';
}
export const characterStudiesOn = (project: OpenedProject) => studyPagesOn(project, 'character');

// MARK: Presence (the index's presence table)

const byRecord = new WeakMap<object, { entities: object; rows: Presence[] }>();

/** A fragment's presence (`fragmentPresence`), worked out again only when the fragment or the cast changed. */
export function presenceOf(project: OpenedProject, fragmentId: string): Presence[] {
  const f = project.fragmentRecord(fragmentId);
  if (!f) return [];
  const hit = byRecord.get(f);
  if (hit && hit.entities === project.record.entities) return hit.rows;
  const rows = presence(f.text, reference(f.metadata, 'pov'), explicitPresence(f.presence), project.record.entities, f.kind);
  byRecord.set(f, { entities: project.record.entities, rows });
  return rows;
}

/** The entities marked absent by hand in a fragment (listed in the inspector so they can go back to automatic). */
export function markedAbsent(project: OpenedProject, fragmentId: string): string[] {
  const f = project.fragmentRecord(fragmentId);
  return f ? Object.entries(f.presence).filter(([, v]) => v === 'absent').map(([k]) => k) : [];
}

export interface StoryModel {
  rows: PresenceRow[];
  appearances: Map<string, { scenes: number; pov: number }>;
  /** Characters, most present in the manuscript first. */
  people: OpenedProject['record']['entities'];
  /** Who is in which manuscript scene, in reading order. */
  co: CoPresence;
}

const models = new WeakMap<object, StoryModel>();

/** The room's per-snapshot figures (`StoryMemo`): presence rows scanned once per rebuild of the project. */
export function storyModel(project: Project): StoryModel {
  let m = models.get(project);
  if (m) return m;
  const p = opened(project);
  const rows: PresenceRow[] = [];
  for (const id of p.fragmentIds().sort()) for (const r of presenceOf(p, id)) rows.push({ fragmentID: id, ...r });
  m = { rows, appearances: storyAppearances(p, rows), people: storyPeople(p, rows), co: manuscriptCoPresence(p, rows) };
  models.set(project, m);
  return m;
}

const sceneLists = new WeakMap<object, Map<string, StoryScene[]>>();

/** The arrangement's scenes in narrative order with story times (estimated between tagged ones): the manuscript, or an
 *  alternate order being tried; notes and the Trash are never charted. */
export function storyScenesOf(project: Project, arrangement: ArrangementInfo | null): StoryScene[] {
  const id = arrangement && (arrangement.role === 'manuscript' || arrangement.role === 'alternate') ? arrangement.id : (project.manuscriptId() ?? '');
  let byArrangement = sceneLists.get(project);
  if (!byArrangement) sceneLists.set(project, (byArrangement = new Map()));
  let scenes = byArrangement.get(id);
  if (!scenes) byArrangement.set(id, (scenes = storyScenes(opened(project), id || undefined)));
  return scenes;
}

export const calendarLanesOf = (project: Project, scenes: StoryScene[]) => calendarLanes(scenes, storyModel(project).rows, opened(project).record.entities);
export const chronologyOf = (project: Project, scenes: StoryScene[], grouping: ChronologyGrouping, estimated: boolean) =>
  chronology(scenes, storyModel(project).rows, opened(project).record.entities, grouping, estimated);

/** Names in the cast, lowercased (names and aliases), for the name generator's ticks. */
export function castNames(project: Project): Set<string> {
  return new Set(opened(project).record.entities.flatMap((e) => [e.name, ...e.aliases]).map((n) => n.toLowerCase()));
}

// MARK: Continuity

/** The device's language, for the dates in findings (the Mac writes them in the device's). */
export const deviceLocale = () => (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

/** `continuityFindings`: the manuscript's scenes checked against their story data, how people travel in the story and
 *  the beat sheet. */
export function continuityFindings(project: Project, locale = deviceLocale()): ContinuityFinding[] {
  const p = opened(project);
  return checkContinuity(sceneFacts(p), p.record.entities, { beatSheet: readBeatSheet(p.record), locale, travel: travelOf(project) });
}

// MARK: Travel

/** `project.travel`: how people travel in the story (today's transport when never set). */
export const travelOf = (project: Project): TravelSettings => readTravel(opened(project).record.settings);

/** `setTravel`: Travel in the Story… ("Travel in the story"); nothing when it's what's stored. Returns whether it changed. */
export function setTravel(live: LiveProject, travel: TravelSettings): boolean {
  return storyEdit(live, (e) => e.setTravel(travel)).length > 0;
}

/** Comment ids of findings already in the margin (`continuity-<id>`). */
export function continuityInMargin(project: Project, findings: ContinuityFinding[]): Set<string> {
  const p = opened(project);
  const out = new Set<string>();
  for (const f of findings) if (p.fragmentRecord(f.fragmentID)?.comments.some((c) => c.id === 'continuity-' + f.id)) out.add(f.id);
  return out;
}

/** `addToMargin`: findings as comments from "Continuity check", each only once. Returns how many were added. */
export function addContinuityToMargin(live: LiveProject, findings: ContinuityFinding[]): number {
  return live.edit((e) => {
    const d = e as DocumentEditor;
    return new ReviseEditor(d.docs, d.context).addContinuityToMargin(findings);
  });
}

/** A finding's passage as Write selects it (UTF-16 in its text). */
export function findingPassage(project: Project, f: ContinuityFinding): { fragmentId: string; from: number; to: number } {
  const text = project.fragment(f.fragmentID)?.text ?? '';
  return { fragmentId: f.fragmentID, from: utf16OffsetOfScalar(text, f.range[0]), to: utf16OffsetOfScalar(text, f.range[1]) };
}

// MARK: Story times

/** `storyTimeSuggestions(start:)`: suggestions for the manuscript's untimed scenes, from their openings. */
export function storyTimeSuggestions(project: Project, start: number | null): StoryTimeProposal[] {
  return suggestStoryTimes(storyTimeScenes(sceneFacts(opened(project))), { start });
}

/** `hasStoryTimes`: whether any manuscript scene has a story time yet (else relative cues need a start). */
export function hasStoryTimes(project: Project): boolean {
  const p = opened(project);
  return p.counted(false).some((id) => p.fragment(id)?.metadata['story-time']?.type === 'date');
}

/** `applyStoryTimes`: the accepted times, after a "Before setting story times" checkpoint. Returns how many were set. */
export function applyStoryTimes(live: LiveProject, times: Map<string, Date>): number {
  return storyEdit(live, (e) => e.setStoryTimes(times));
}

/** Snapped to the nearest quarter hour (Chronology's drag). */
export const snapQuarter = (ms: number) => Math.round(ms / 900_000) * 900_000;

// MARK: Shape

const shapes = new WeakMap<object, SceneShape | null>();

/** `sceneShape`: the manuscript's scenes measured (prose only; null for a screenplay), once per rebuild. */
export function shapeOf(project: Project): SceneShape | null {
  if (shapes.has(project)) return shapes.get(project)!;
  const p = opened(project);
  const shape = sceneShapeOf(p, p.record.locale.replace(/_/g, '-'));
  shapes.set(project, shape);
  return shape;
}

/** The Shape's CSV, with POV names. */
export function shapeCSVOf(project: Project, shape: SceneShape): string {
  const names = new Map(opened(project).record.entities.map((e) => [e.id, e.name]));
  return shapeCSV(shape, (id) => (id ? (names.get(id) ?? '') : ''));
}

/** Saves text as a file: the shell's Save dialog, else the browser's download (host/host.ts). */
export function downloadText(name: string, text: string, mime = 'text/csv') {
  void saveFile(name, new TextEncoder().encode(text), `${mime};charset=utf-8`);
}

/** A file name from a title (no path characters). */
export const fileName = (title: string, suffix: string) => `${title.replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ').trim() || 'Book'}${suffix}`;

// MARK: Per-browser choices (the Mac's @AppStorage and UserDefaults on this device)

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(`ws:${key}`);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(`ws:${key}`, JSON.stringify(value));
  } catch {
    // kept for this page only
  }
}

export interface StoryPrefs {
  /** Timeline: by story time (calendar lanes) or by reading order (`timelineByStoryTime`). */
  byStoryTime: boolean;
  /** Chronology's lanes and whether estimated times show (`chronologyLanes`, `chronologyEstimated`). */
  lanes: ChronologyGrouping;
  estimated: boolean;
  /** The Suggest Story Times sheet, opened from a page's header. */
  suggesting: boolean;
}

let prefs: StoryPrefs = {
  byStoryTime: read('timelineByStoryTime', true),
  lanes: ((l: unknown) => (l === 'setting' || l === 'character' ? l : 'pov'))(read('chronologyLanes', 'pov')),
  estimated: read('chronologyEstimated', true),
  suggesting: false,
};
const prefListeners = new Set<() => void>();

export function setStoryPrefs(change: Partial<StoryPrefs>) {
  prefs = { ...prefs, ...change };
  if ('byStoryTime' in change) write('timelineByStoryTime', prefs.byStoryTime);
  if ('lanes' in change) write('chronologyLanes', prefs.lanes);
  if ('estimated' in change) write('chronologyEstimated', prefs.estimated);
  for (const fn of prefListeners) fn();
}

/** The room's view choices, shared by a page and its header controls. */
export function useStoryPrefs(): StoryPrefs {
  return useSyncExternalStore(
    (fn) => {
      prefListeners.add(fn);
      return () => prefListeners.delete(fn);
    },
    () => prefs,
  );
}

// MARK: The name generator's choices and the writer's own lists (per browser: `nameGeneratorOptions`/`…Lists`)

export function loadNameOptions(): NameOptions {
  const raw = read<unknown>('nameGeneratorOptions', null);
  return raw ? decodeNameOptions(raw) : defaultNameOptions();
}

export const saveNameOptions = (o: NameOptions) => write('nameGeneratorOptions', o);

const KINDS: NameListKind[] = ['female', 'male', 'anyone', 'surnames'];

export function loadOwnLists(): NameList[] {
  const raw = read<unknown>('nameGeneratorLists', []);
  if (!Array.isArray(raw)) return [];
  return raw.flatMap((l): NameList[] => {
    if (!l || typeof l !== 'object') return [];
    const o = l as Record<string, unknown>;
    if (typeof o.id !== 'string' || typeof o.origin !== 'string' || !KINDS.includes(o.kind as NameListKind) || !Array.isArray(o.names)) return [];
    const names = (o.names as unknown[]).filter((n): n is string => typeof n === 'string');
    return names.length ? [{ id: o.id, origin: o.origin, kind: o.kind as NameListKind, names }] : [];
  });
}

export const saveOwnLists = (lists: NameList[]) => write('nameGeneratorLists', lists);

/** A list of the writer's own (`NameList(origin:kind:names:)`: a fresh id). */
export function ownList(origin: string, kind: NameListKind, text: string): NameList {
  const id = `own.${typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;
  return { id, origin: origin.trim(), kind, names: parseNames(text) };
}

export const allNameLists = (own: NameList[]) => [...BUILT_IN_LISTS, ...own];
